import React from "react";
import { Link } from "react-router-dom";
import "./HerlevSeo.css";
import ConsultationSection from "./ConsultationSection";
import Seo from "../../../Seo/Seo";
import {
    webPageSchema,
    faqPageSchema,
    serviceSchema,
} from "../../../Seo/jsonld";
import { getRouteSeo } from "../../../Seo/routeSeo";

// The title and meta description were previously written straight onto
// document.head inside a useEffect. <Seo /> owns them now, so they are part of
// the React render and get captured by the prerenderer like every other page's.
const PATH = "/skolemad-herlev";


const HERLEV_FAQS = [
    [
        "Kan man bestille skolemad i Herlev?",
        "Ja. Onlineskolemad har adresse i Herlev og tilbyder online bestilling af skolemad. Om en bestemt skole er tilknyttet ordningen, afhænger af den konkrete skole og aftale. Hvis en skole ikke findes på platformen, opfordrer Onlineskolemad skolen til at kontakte virksomheden.",
    ],
    [
        "Hvilken type mad kan man bestille?",
        "Sortimentet omfatter blandt andet varme retter, sandwich, baguettes, madpakker, pastasalater, frugt og drikkevarer. Der findes også vegetariske og veganske muligheder samt andre kostrelaterede filtre i appen.",
    ],
    [
        "Kan jeg bestille mad til flere børn?",
        "Ja. Onlineskolemad oplyser, at systemet kan bruges til at bestille mad til flere børn fra samme brugerprofil.",
    ],
    [
        "Hvor langt frem kan jeg bestille skolemad?",
        "Onlineskolemad oplyser, at der kan bestilles op til tre måneder frem.",
    ],
    [
        "Kan en bestilling ændres eller afbestilles?",
        "Ja. Du kan ændre din bestilling frem til kl. 06.00 på leveringsdagen. Hvis du ønsker at afbestille bestillingen, kan det ske frem til kl. 09.00 same dag.",
    ],
    [
        "Tilbyder Onlineskolemad mad til andre end skoler?",
        "Ja. Virksomheden er registreret inden for event catering, og Onlineskolemad kan derfor også kontaktes om catering og forplejning til andre sammenhænge. Den konkrete løsning bør aftales direkte med virksomheden.",
    ],
    [
        "Kan min skole i Herlev begynde at tilbyde Onlineskolemad?",
        "Det er muligt at kontakte Onlineskolemad og undersøge mulighederne. Virksomheden oplyser selv, at skoler, som ikke findes på listen, kan bede skolen kontakte dem, hvorefter mulighederne for en aftale kan undersøges.",
    ],
];

const HerlevSeo = () => {
    const menuItems = [
        ["Varme retter", "Dagens varme ret giver mulighed for et mere traditionelt frokostmåltid og skifter løbend."],
        ["Sandwich og baguettes", "Et praktisk valg til elever, der foretrækker en kold frokost."],
        ["Madpakker", "Færdige madpakker kan være en enkel løsning, når frokosten skal være klar til skoledagen."],
        ["Salater og andre kolde retter", "Udvalget omfatter blandt andet pastasalater og andre kolde måltider."],
        ["Frugt og drikkevarer", "Frugt og drikkevarer kan bestilles sammen med maden, så frokosten kan samles ét sted."],
        ["Flere kostbehov", "Filtrer efter vegetariske, veganske og andre behov i appen."],
    ];

    const audience = [
        ["Skolefrokost til elever", "En skolefrokost skal være nem at bestille og nem at få udleveret. Onlineskolemad tilbyder både varme retter og forskellige kolde alternativer, så eleverne ikke behøver vælge det samme hver dag. Udvalget kan blandt andet omfatte sandwich, pastasalater, madpakker, frugt og dagens varme ret."],
        ["Madordning til skoler", "En madordning kan være relevant for skoler, der ønsker at gøre frokosten lettere tilgængelig for elever og forældre.Onlineskolemad har et digitalt bestillingssystem, hvor forældre kan administrere bestillinger til deres børn. Systemet gør det også muligt at håndtere flere børn fra samme konto og holde overblik over bestillinger."],
        ["Frokostordning", "En frokostordning kan være relevant, når der er behov for en fast og praktisk måde at få mad ind i hverdagen.For skoler og andre organisationer kan løsningen tilpasses den konkrete struktur, hvor Onlineskolemad kan kontaktes for at undersøge mulighederne for en aftale. Virksomheden skriver selv, at skoler, som ikke allerede findes på platformen, kan kontakte dem med henblik på at etablere en aftale."],
        ["Kantine og salg på skolen", "Nogle skoler har mulighed for at sælge mad direkte på skolen. Onlineskolemad oplyser, at der på skoler, hvor skolen tillader det, også kan sælges mad på stedet med betaling via kort, MobilePay eller kontant. Det kan være et supplement til forudbestilling og give eleverne en ekstra mulighed for at købe frokost eller andre varer i løbet af skoledagen."],
        ["Catering og forplejning", "Onlineskolemad er registreret inden for event catering, og virksomhedens aktiviteter omfatter også mad til andre sammenhænge end den almindelige skolefrokost. Det gør catering og forplejning relevant for eksempelvis arrangementer, møder og andre situationer, hvor der skal leveres mad til flere personer. Hvis du har brug for mad til et arrangement i Herlev, kan du kontakte Onlineskolemad for at høre, hvilke muligheder der passer til behovet."],
    ];

    const reasons = [
        ["En løsning udviklet omkring skolehverdagens behov", "Onlineskolemad har arbejdet med skolemad siden 2011 og beskriver selv, at virksomheden er udviklet med fokus på at gøre det lettere for forældre at give deres børn en god frokost i skolen.", "fa-school"],
        ["Flere typer frokost", "Elever har forskellige præferencer. Derfor består sortimentet ikke kun af én type skolefrokost, men af både varme retter, sandwich, madpakker, salater, frugt og drikkevarer.", "fa-utensils"],
        ["Digital bestilling", "Forældre kan administrere deres barns skolemad online, bestille frem i tiden og ændre eller afbestille inden den gældende frist.", "fa-mobile-alt"],
        ["Mulighed for forskellige kostbehov", "Onlineskolemad oplyser, at der findes vegetariske, veganske, keto- og andre retter, samt mulighed for filtrering efter allergier i appen.", "fa-leaf"],
        ["Lokal tilstedeværelse", "Med adresse i Herlev er virksomheden fysisk placeret i den by, som denne side henvender sig til. Det giver en naturlig lokal tilknytning til kunder i området.", "fa-map-marker-alt"],
    ];

    return (
        <main className="herlev-page remove-space">
            <Seo
                path={PATH}
                jsonLd={[
                    webPageSchema({
                        title: getRouteSeo(PATH)?.title,
                        description: getRouteSeo(PATH)?.description,
                        url: PATH,
                    }),
                    serviceSchema({
                        name: "Skolemad i Herlev",
                        description: getRouteSeo(PATH)?.description,
                        url: PATH,
                        areaServed: "Herlev",
                    }),
                    // Accurate: these questions and answers are the ones
                    // rendered in the FAQ section below, from HERLEV_FAQS.
                    faqPageSchema(
                        HERLEV_FAQS.map(([question, answer]) => ({ question, answer }))
                    ),
                ]}
            />
            <section className="herlev-hero">
                <div className="herlev-hero-shape herlev-hero-shape-one" />
                <div className="herlev-hero-shape herlev-hero-shape-two" />
                <div className="container">
                    <div className="row align-items-center">
                        <div className="col-lg-7">
                            <div className="herlev-hero-copy wow fadeInUp">
                                <span className="herlev-kicker">Skolemad i Herlev til skoler, børn og familier</span>
                                <h1>Skolemad i Herlev, der gør hverdagen nemmere</h1>
                                <div className="herlev-rule"><span /><span /></div>
                                <p className="herlev-lead">
                                    Onlineskolemad tilbyder nem skolemad i Herlev med varme og kolde retter, sandwich, madpakker, frugt og drikkevarer, som bestilles online. Med adresse på Herlev Bygade 27, 2730 Herlev har virksomheden siden 2011 leveret skolemad på hverdage med egne chauffører, hvilket gør frokost nemt for både forældre og skoler.
                                </p>
                                <div className="herlev-actions">
                                    <Link className="btn btn-1 gap-1" to="/menu">Bestil skolemad <i className="fas fa-arrow-right" /></Link>
                                    <Link className="herlev-text-link" to="/contact">Kontakt Onlineskolemad <i className="fas fa-arrow-right" /></Link>
                                </div>
                                <div className="herlev-trust-row">
                                    <span><i className="fas fa-check-circle" /> Egne chauffører</span>
                                    <span><i className="fas fa-check-circle" /> Online bestilling</span>
                                    <span><i className="fas fa-check-circle" /> Halal</span>
                                </div>
                                <div className="flex-group play-app row">
                                    <div className="col-md-6 mt-md-0 mt-4 mb-md-0 mb-5">
                                        <Link className="btn btn-dark google_ply mb-3" target="blank" to={'https://apps.apple.com/dk/app/onlineskolemad/id6741146828?l=da'}>
                                            <span className="play_img">
                                                <img src="/assets/img/apple-icon.svg" alt="Apple App Store" />
                                            </span>
                                            <div className="download-cont">
                                                <span>Download i</span>
                                                <p>Appstore</p>
                                            </div>
                                        </Link>
                                        <img className="qr-img" src="/assets/img/qr-ios.png" alt="QR-kode til Onlineskolemad i App Store" />
                                    </div>
                                    <div className="col-md-6">
                                        <Link className="btn btn-dark google_ply mb-3" target="blank" to={'http://play.google.com/store/apps/details?id=com.onlineskolemad&hl=en'}>
                                            <span className="play_img">
                                                <img src="/assets/img/palystore-icon.svg" alt="Google Play" />
                                            </span>
                                            <div className="download-cont">
                                                <span>Hent den i</span>
                                                <p>Google Play</p>
                                            </div>
                                        </Link>
                                        <img className="qr-img" src="/assets/img/qr-android.png" alt="QR-kode til Onlineskolemad i Google Play" />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-lg-5">
                            <div className="herlev-hero-image wow fadeInRight">
                                <img src="/assets/img/childrens-2.png" alt="Børn, der nyder skolemad" />
                                <div className="herlev-hero-note"><strong>2730</strong><span>Herlev</span></div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="herlev-intro sec-pd">
                <div className="container">
                    <div className="row align-items-center">
                        <div className="col-lg-6">
                            <div className="herlev-photo-frame wow fadeInLeft">
                                <img src="/assets/img/food-1.png" alt="Frisk skolemad fra Onlineskolemad" />
                                <span className="herlev-photo-stamp"><i className="fas fa-heart" /> Gør frokosten lettere</span>
                            </div>
                        </div>
                        <div className="col-lg-6">
                            <div className="herlev-copy-block wow fadeInUp">
                                <span className="herlev-eyebrow">En enklere skoledag</span>
                                <h2>Mad til skoler og familier i Herlev</h2>
                                <div className="herlev-rule"><span /><span /></div>
                                <p>
                                    En skolefrokost skal fungere i en travl hverdag. Forældrene skal kunne bestille uden besvær, eleverne skal kunne vælge noget, de faktisk har lyst til at spise, og skolen skal kunne håndtere udleveringen på en overskuelig måde.
                                </p>
                                <p>
                                    Det er netop den type hverdag, Onlineskolemad er bygget til.
                                </p>
                                <p>
                                    På virksomhedens nuværende platform kan forældre oprette en bruger, vælge barn, skole og klasse og bestille mad online. Der kan bestilles op til tre måneder frem, og bestillinger kan ændres eller afbestilles inden den gældende frist.
                                </p>
                                <p>
                                    Udvalget omfatter blandt andet:
                                </p>
                                <div className="herlev-trust-row-learn">
                                    <span><i className="fas fa-check-circle" /> Varme retter</span>
                                    <span><i className="fas fa-check-circle" /> Sandwich</span>
                                    <span><i className="fas fa-check-circle" /> Baguettes</span>
                                    <span><i className="fas fa-check-circle" /> Madpakker</span>
                                    <span><i className="fas fa-check-circle" /> Grovflutes</span>
                                    <span><i className="fas fa-check-circle" /> Rugbrødsmadder</span>
                                    <span><i className="fas fa-check-circle" /> Pastasalater</span>
                                    <span><i className="fas fa-check-circle" /> Frugt</span>
                                    <span><i className="fas fa-check-circle" /> Drikkevarer</span>
                                </div>

                                <p>
                                    Der er også mulighed for at filtrere efter blandt andet vegetariske og veganske retter samt andre kostbehov i Onlineskolemad-appen. Virksomheden oplyser desuden, at maden er halal.
                                </p>
                                <Link className="btn btn-1" to="/about">Læs mere om os</Link>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="sec-pd bg-gray position-relative">
                <div className="container">
                    <div className="row align-items-center">
                        <div className="col-md-6 mmb-20">
                            <div>
                                <div className="title-1 wow fadeInUp" data-wow-delay="0.1s">
                                    <h2>
                                        Hvorfor skolemad er relevant i Herlev
                                    </h2>
                                    <div className="herlev-rule"><span /><span /></div>
                                    <p>
                                        Herlev har en særlig kombination af børnefamilier, skoler, dagtilbud og et betydeligt lokalt erhvervsliv. Kommunen oplyser, at mere end 30.000 borgere og omkring 26.000 arbejdspladser hører hjemme i Herlev. Samtidig beskriver kommunen, at mange børnefamilier vælger Herlev på grund af blandt andet plads, infrastruktur og nærheden til København.
                                    </p>
                                    <p>
                                        Det gør en velfungerende madløsning relevant for mange forskellige hverdage.
                                        For en familie kan skolemad være en måde at slippe for at smøre madpakker hver morgen. For en skole kan en ekstern madordning gøre det lettere at tilbyde eleverne forskellige frokostmuligheder uden selv at skulle stå for hele madproduktionen.
                                    </p>
                                    <p>
                                        Herlev Kommune har fra 1. august 2026 fem folkeskoler og en specialskole, og på skolerne findes der også SFO-tilbud. Kommunen har desuden en række dagtilbud for børn.
                                    </p>
                                    <p>
                                        Det betyder, at behovet for praktiske og fleksible måltidsløsninger ikke kun handler om én type skole eller familie.

                                    </p>
                                    <div className="flex-group play-app row">
                                        <div className="col-md-6 mt-md-0 mt-4 mb-md-0 mb-5">
                                            <Link className="btn btn-dark google_ply mb-3" target="blank" to={'https://apps.apple.com/dk/app/onlineskolemad/id6741146828?l=da'}>
                                                <span className="play_img">
                                                    <img src="/assets/img/apple-icon.svg" alt="Apple App Store" />
                                                </span>
                                                <div className="download-cont">
                                                    <span>Download i</span>
                                                    <p>Appstore</p>
                                                </div>
                                            </Link>
                                            <img className="qr-img" src="/assets/img/qr-ios.png" alt="QR-kode til Onlineskolemad i App Store" />
                                        </div>
                                        <div className="col-md-6">
                                            <Link className="btn btn-dark google_ply mb-3" target="blank" to={'http://play.google.com/store/apps/details?id=com.onlineskolemad&hl=en'}>
                                                <span className="play_img">
                                                    <img src="/assets/img/palystore-icon.svg" alt="Google Play" />
                                                </span>
                                                <div className="download-cont">
                                                    <span>Hent den i</span>
                                                    <p>Google Play</p>
                                                </div>
                                            </Link>
                                            <img className="qr-img" src="/assets/img/qr-android.png" alt="QR-kode til Onlineskolemad i Google Play" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="img-style-3 pl-2per img-res-md wow fadeInRight">
                                <img src="/assets/img/download-mobile.png" alt="Onlineskolemad-appen på en mobiltelefon" loading="lazy" />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="download-banner img-res-md">
                    <img src="/assets/img/download-app.png" alt="Download Onlineskolemad-appen" loading="lazy" />
                </div>
            </section>

            <section className="herlev-pale-section sec-pd">
                <div className="container">
                    <div className="herlev-section-heading">
                        <span className="herlev-eyebrow">Noget for enhver frokost</span>
                        <h2>Hvad kan du bestille?</h2>
                        <p>Onlineskolemad har et bredt udvalg, så skolefrokosten kan variere fra dag til dag.</p>
                    </div>
                    <div className="row">
                        {menuItems.map(([title, text], index) => (
                            <div className="col-md-6 col-lg-4" key={title}>
                                <article className="herlev-menu-item wow fadeInUp" data-wow-delay={`${index * 0.08}s`}>
                                    <span className="herlev-menu-icon"><i className={`fas ${["fa-utensils", "fa-bread-slice", "fa-box", "fa-leaf", "fa-glass-whiskey", "fa-seedling"][index]}`} /></span>
                                    <div><h3>{title}</h3><p>{text}</p></div>
                                </article>
                            </div>
                        ))}
                        <p>
                            Onlineskolemad oplyser desuden, at sortimentet indeholder vegetariske, veganske og andre specialtilpassede muligheder, og at brugerne kan filtrere efter allergier og kostbehov i appen.
                        </p>
                    </div>
                </div>
            </section>

            <section className="herlev-audience sec-pd">
                <div className="container">
                    <div className="row align-items-end mb-4">
                        <div className="col-lg-8">
                            <span className="herlev-eyebrow">En løsning til hverdagen</span>
                            <h2>Skolemad i Herlev til forskellige behov</h2>
                        </div>
                        <div className="col-lg-4"><p className="herlev-heading-note">Fra den enkelte madpakke til en komplet madordning for skolen.</p></div>
                    </div>
                    <div className="row">
                        {audience?.map(([title, text], index) => (
                            <div className="col-md-4" key={title}>
                                <article className="herlev-audience-card"><span>0{index + 1}</span><h3>{title}</h3><p>{text}</p><Link to="/contact">vide mere <i className="fas fa-arrow-right" /></Link></article>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="herlev-steps sec-pd">
                <div className="container">
                    <div className="row align-items-center">
                        <div className="col-lg-5">
                            <div className="herlev-copy-block">
                                {/* <span className="herlev-eyebrow">Sådan fungerer det</span> */}
                                <h2>Sådan fungerer skolemad fra Onlineskolemad</h2>
                                <div className="herlev-rule"><span /><span /></div>
                                <p>Bestilling er bygget op omkring en enkel digital proces.</p>
                                <Link className="btn btn-1" to="/signup">Opret bruger</Link>
                            </div>
                        </div>
                        <div className="col-lg-7">
                            <div className="herlev-step-list">
                                {["Opret en bruger", "Vælg maden", "Betal online", "Maden klargøres og leveres", "Eleven får sin frokost"].map((step, index) => (
                                    <div className="herlev-step" key={step}>
                                        <span>0{index + 1}</span>
                                        <div>
                                            <h3>{step}</h3>
                                            <p>{["Forælderen opretter en konto og tilføjer barnet med relevant skole og klasse.", "Vælg dato og de retter, barnet skal have. Der kan bestilles flere dage frem, så frokosten kan planlægges på forhånd.", "Der indsættes penge på madkontoen, som derefter bruges til bestillinger. Onlineskolemad tilbyder flere betalingsmuligheder, herunder kort og MobilePay.", "Maden forberedes til den valgte skoledag. Onlineskolemad oplyser, at virksomhedens egne chauffører står for den daglige levering.", "Madpakkerne mærkes med oplysninger, så det er tydeligt, hvem maden tilhører."][index]}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="herlev-audience sec-pd">
                <div className="container">
                    <ConsultationSection />
                </div>
            </section>

            <section className="herlev-reasons sec-pd">
                <div className="container">
                    <div className="herlev-reasons-heading">
                        <div>
                            <span className="herlev-eyebrow">Det lokale valg</span>
                            <h2>Hvorfor vælge Onlineskolemad?</h2>
                        </div>
                        <p>En madløsning skabt til skolehverdagens rytme, forskellige børn og familier i Herlev.</p>
                    </div>
                    <div className="herlev-reasons-grid">
                        {reasons.map(([title, text, icon], index) => (
                            <article className="herlev-reason-card" key={title}>
                                <div className="herlev-reason-number">0{index + 1}</div>
                                <div className="herlev-reason-icon"><i className={`fas ${icon}`} /></div>
                                <div>
                                    <h3>{title}</h3>
                                    <p>{text}</p>
                                </div>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            <section className="sec-pd rel-z-1">
                <div className="top-bottom-shaps">
                    <span className="shap shap-1" />
                    <span className="shap shap-2" />
                </div>
                <div className="container">
                    <div className="row align-items-center">
                        <div className="col-md-6 mmb-20">
                            <div className="img-style-1 wow fadeInLeft">
                                <img src="/assets/img/about-1.png" alt="Skolemad i Herlev klar til levering" loading="lazy" />
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div>
                                <div
                                    className="title-1 wow fadeInRight"
                                    data-wow-delay="0.1s"
                                    style={{
                                        visibility: "visible",
                                        animationDelay: "0.1s",
                                        animationName: "fadeInUp",
                                    }}
                                >
                                    <h2 className="title-1-h">Hvem kan bruge Onlineskolemad i Herlev?</h2>
                                    <div className="herlev-rule"><span /><span /></div>
                                    <p>
                                    </p>
                                    <div className="mt-3">
                                        {/* <h2>Our Vision</h2> */}
                                        <p>
                                            Løsningen er først og fremmest relevant for skoler og skolefamilier, men virksomhedens cateringaktiviteter gør også tilbuddet relevant i andre sammenhænge.
                                        </p>
                                        <p>Det kan eksempelvis være:</p>
                                        <ul>
                                            <li><strong>Skoler</strong>, der ønsker en praktisk løsning til skolemad.</li>
                                            <li><strong>Forældre</strong>, der vil bestille frokost til deres børn.</li>
                                            <li><strong>Institutioner</strong>, hvor der er behov for mad til flere børn eller deltagere.</li>
                                            <li><strong>Virksomheder</strong>, der ønsker mad eller forplejning til medarbejdere eller arrangementer.</li>
                                            <li><strong>Organisationer</strong>, der har brug for mad til et møde eller arrangement.</li>
                                            <li><strong>Arrangører</strong>, der ønsker catering til en aktivitet i Herlev.</li>
                                        </ul>
                                        <p>Den konkrete løsning afhænger af behov, antal personer og den aftale, der kan etableres.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="herlev-faq sec-pd">
                <div className="container">
                    <div className="herlev-section-heading"><span className="herlev-eyebrow">Spørgsmål og svar</span><h2>Ofte stillede spørgsmål om skolemad i Herlev</h2></div>
                    <div className="herlev-faq-list">
                        {HERLEV_FAQS.map(([question, answer], index) => (
                            <details key={question} open={index === 0}>
                                <summary>{question}<i className="fas fa-plus" /></summary>
                                <p>{answer}</p>
                            </details>
                        ))}
                    </div>
                </div>
            </section>

            <section className="herlev-cta">
                <div className="container">
                    <div className="herlev-cta-inner"><div>
                        <span className="herlev-eyebrow">Lokalt forankret i Herlev</span>
                        <h2>Skal vi finde en madløsning til jeres skole eller organisation?</h2>
                        <p>Uanset om du er forælder, skolemedarbejder eller ansvarlig for en organisation, er næste skridt enkelt.</p>
                        <p>Kontakt <strong>Onlineskolemad</strong> for at høre om mulighederne for skolemad, frokostordning, levering eller catering i Herlev.</p>
                        <p>Med lokal adresse i Herlev, online bestilling og et udvalg af både varme og kolde frokostmuligheder kan Onlineskolemad være et praktisk udgangspunkt, når maden skal fungere i en travl hverdag.</p>
                    </div>
                        <div className="herlev-cta-actions">
                            <Link className="btn btn-1" to="/contact">Kontakt Onlineskolemad</Link>
                            <a href="tel:26227469">Telefon: 26 22 74 69</a>
                            <a href="/">Adresse: Herlev Bygade 27, 2730 Herlev</a>
                            <a href="/">E-mail: onlineskolemad@hotmail.com</a>
                        </div>
                    </div>
                </div>
            </section>
        </main>
    );
};

export default HerlevSeo;